import { extractAroFromSku, isModeloComMedida, resolveModeloJoia } from "./skuUtils";

/**
 * moldRingUtils.js — Módulo de Inteligência para Gestão de Medidas e Aros em Moldes de Borracha
 * 
 * Evita 100% dos falsos positivos na produção de anéis e alianças:
 * - Identifica se o modelo da peça utiliza medidas (Aro).
 * - Extrai o aro solicitado pelo pedido (specs ou SKU).
 * - Compara contra a matriz de aros físicos que a fábrica possui (config_borracha.aros_disponiveis).
 * - Avalia tolerância de ajuste de bancada configurada (ex: ±1 número).
 * - Se não houver molde compatível, sinaliza para seguir no fluxo de Impressão 3D (resina).
 */

// Grade padrão de aros para o mercado nacional de joalheria
export const AROS_PADRAO_GRADE = [
  10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 
  21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32
];

// Presets rápidos para a bancada
export const AROS_MAIS_VENDIDOS = [14, 16, 18, 20, 22];
export const AROS_PARES = [12, 14, 16, 18, 20, 22, 24, 26];

/**
 * Avalia se um item/pedido possui molde de borracha compatível com o aro solicitado.
 * 
 * @param {Object|string} orderOrSku - Objeto do pedido ou string de SKU
 * @param {Object} moldConfig - Objeto config_borracha da peça (ou null)
 * @param {Object} options - Opções adicionais ({ catItem, tipoModeloFallback })
 * @returns {Object} Resultado detalhado da avaliação
 */
export function avaliarDisponibilidadeMolde(orderOrSku, moldConfig = null, options = {}) {
  const sku = typeof orderOrSku === "string" ? orderOrSku : orderOrSku?.sku || "";
  
  // 1. Determina o modelo da peça
  const tipoModelo = moldConfig?.tipo_modelo || 
                     options.tipoModeloFallback || 
                     resolveModeloJoia(sku, options.catItem, null);

  // 2. Se NÃO for anel/aliança, é produto sem aro (Pingente, Berloque, etc.)
  if (!isModeloComMedida(tipoModelo)) {
    return {
      disponivel: true,
      tipoMatch: "padrao_sem_medida",
      isAnel: false,
      aroSolicitado: null,
      aroMolde: null,
      instrucao: null,
      motivo: "Produto sem variação de aro (padrão)"
    };
  }

  // 3. É ANEL OU ALIANÇA — Extrai o aro do pedido
  let aro = null;
  if (typeof orderOrSku === "object" && orderOrSku !== null) {
    if (orderOrSku.specs?.size) {
      const parsed = parseFloat(orderOrSku.specs.size);
      if (!isNaN(parsed)) aro = parsed;
    } else if (orderOrSku.specs?.aro) {
      const parsed = parseFloat(orderOrSku.specs.aro);
      if (!isNaN(parsed)) aro = parsed;
    }
  }

  // Fallback: extrai do sufixo do SKU
  if (aro === null) {
    aro = extractAroFromSku(sku);
  }

  // Se for anel mas não tiver aro especificado (ex: SKU Pai genérico), permite com aviso
  if (aro === null) {
    return {
      disponivel: true,
      tipoMatch: "anel_sem_aro_especificado",
      isAnel: true,
      aroSolicitado: null,
      aroMolde: null,
      instrucao: "Aro não especificado",
      motivo: "SKU ou pedido sem especificação de aro"
    };
  }

  // 4. Analisa a matriz de aros cadastrada na borracha
  const arosDisponiveis = Array.isArray(moldConfig?.aros_disponiveis)
    ? moldConfig.aros_disponiveis.map(Number).filter(n => !isNaN(n))
    : [];

  // Se o molde foi cadastrado como anel mas ainda não tem a grade definida,
  // mantém retrocompatibilidade assumindo que a oficina atende
  if (arosDisponiveis.length === 0) {
    return {
      disponivel: true,
      tipoMatch: "generico_todos_aros",
      isAnel: true,
      aroSolicitado: aro,
      aroMolde: aro,
      instrucao: `Aro ${aro} (Molde genérico)`,
      motivo: "Molde cadastrado sem restrição de aros"
    };
  }

  // 5. MATCH EXATO (A fábrica tem borracha física exatamente para este aro)
  if (arosDisponiveis.includes(aro)) {
    return {
      disponivel: true,
      tipoMatch: "exato",
      isAnel: true,
      aroSolicitado: aro,
      aroMolde: aro,
      diff: 0,
      instrucao: `Aro ${aro} (Molde Exato)`,
      motivo: `Molde físico disponível para Aro ${aro}`
    };
  }

  // 6. MATCH COM TOLERÂNCIA DE AJUSTE NA BANCADA
  const tolerancia = typeof moldConfig?.tolerancia_ajuste_bancada === "number"
    ? moldConfig.tolerancia_ajuste_bancada
    : 0;

  if (tolerancia > 0) {
    let nearestAro = null;
    let minDiff = Infinity;

    arosDisponiveis.forEach(a => {
      const diff = Math.abs(a - aro);
      if (diff < minDiff) {
        minDiff = diff;
        nearestAro = a;
      }
    });

    if (minDiff <= tolerancia && nearestAro !== null) {
      const delta = aro - nearestAro;
      const acaoBancada = delta > 0
        ? `Abrir +${delta} na bancada`
        : `Reduzir ${delta} na bancada`;

      return {
        disponivel: true,
        tipoMatch: "ajuste_bancada",
        isAnel: true,
        aroSolicitado: aro,
        aroMolde: nearestAro,
        diff: delta,
        tolerancia,
        instrucao: `Injetar Molde ${nearestAro} (${acaoBancada})`,
        motivo: `Atendido pelo Molde ${nearestAro} com ajuste na bancada (tolerância ±${tolerancia})`
      };
    }
  }

  // 7. MOLDE INDISPONÍVEL — BLINDAGEM ANTI-FALSO POSITIVO
  // O aro não existe na fábrica nem pode ser ajustado com segurança na bancada.
  // Deve ir obrigatoriamente para a Impressão 3D de Resina!
  return {
    disponivel: false,
    tipoMatch: "indisponivel",
    isAnel: true,
    aroSolicitado: aro,
    aroMolde: null,
    arosDisponiveis,
    instrucao: `Sem molde para Aro ${aro}`,
    motivo: `Aro ${aro} não possui molde na fábrica (Disponíveis: ${arosDisponiveis.join(", ")})`
  };
}

/**
 * Retorna as propriedades de estilização e texto para badges de aro na UI.
 */
export function getBadgeAroProps(resultado) {
  if (!resultado || !resultado.isAnel) return null;

  if (resultado.tipoMatch === "exato") {
    return {
      label: `Aro ${resultado.aroSolicitado} (Molde Exato)`,
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300",
      tooltip: resultado.motivo,
      isWarning: false
    };
  }

  if (resultado.tipoMatch === "ajuste_bancada") {
    return {
      label: `Aro ${resultado.aroSolicitado} ⚠️ [Molde ${resultado.aroMolde}]`,
      badgeClass: "bg-amber-50 text-amber-800 border-amber-300",
      tooltip: resultado.instrucao,
      isWarning: true
    };
  }

  if (resultado.tipoMatch === "generico_todos_aros") {
    return {
      label: `Aro ${resultado.aroSolicitado}`,
      badgeClass: "bg-slate-100 text-slate-700 border-slate-300",
      tooltip: "Molde cadastrado sem grade específica",
      isWarning: false
    };
  }

  if (!resultado.disponivel) {
    return {
      label: `Aro ${resultado.aroSolicitado} ❌ (Sem Molde)`,
      badgeClass: "bg-rose-50 text-rose-700 border-rose-300",
      tooltip: resultado.motivo,
      isWarning: true
    };
  }

  return null;
}
